import {type AxiosError} from "axios";
import {type FirebaseApp} from "firebase/app";
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  UserCredential,
  verifyPasswordResetCode,
  type Auth,
  type User as FirebaseUser,
} from "firebase/auth";
import {event, type Analytics} from "./analytics";
import {useCookie} from "@/@core/composable/useCookie";

class User {
  uid: string | null = null;
  email: string | null = null;
  displayName: string | null = null;

  async get(uid?: string): Promise<{success: boolean; error?: string}> {
    try {
      if (!uid) {
        return {success: false, error: "User ID is required"};
      }
      
      this.uid = uid;
      // Note: This is a placeholder implementation
      // In a real application, you would fetch user data from your backend/database
      // For now, we'll just set the uid to prevent errors
      
      return {success: true};
    } catch (error) {
      console.error("Failed to get user profile:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to get user profile";
      return {success: false, error: errorMessage};
    }
  }
}

/**
 * Represents the authentication service for Firebase.
 */
class Authentication {
  /**
   * The singleton instance of the Authentication class.
   */
  static _instance?: Authentication;

  /**
   * The access token of the currently authenticated user.
   */
  accessToken: string | null = null;

  /**
   * The analytics instance to log events
   */
  analytics: Analytics;

  /**
   * The currently authenticated user.
   */
  user: User | null = null;

  /**
   * The Firebase user object.
   */
  firebaseUser: FirebaseUser | null = null;

  /**
   * Indicates whether the user is logged in or not.
   */
  loggedIn: boolean = false;

  /**
   * The Firebase authentication service.
   */
  service: Auth;

  /**
   * Creates an instance of the Authentication class.
   * @param app - The Firebase app instance.
   */
  constructor(app: FirebaseApp, analytics: Analytics) {
    this.service = getAuth(app);
    this.analytics = analytics;
  }

  /**
   * Returns the singleton instance of the Authentication class.
   * If the instance does not exist, it creates a new one.
   * @param app - The Firebase app instance.
   * @returns The singleton instance of the Authentication class.
   */
  static getInstance(app: FirebaseApp, analytics: Analytics): Authentication {
    if (this._instance !== undefined) {
      return this._instance;
    }
    this._instance = new Authentication(app, analytics);
    return this._instance;
  }

  /**
   * Signs up a new user with the provided email and password.
   * @param email - The email of the user.
   * @param password - The password of the user.
   * @returns A promise that resolves when the sign-up is successful, or rejects with an error.
   */
  async signUp(email: string, password: string): Promise<{success: boolean; user?: FirebaseUser; error?: string}> {
    try {
      // Input validation
      if (!email || !password) {
        return {success: false, error: "Email and password are required"};
      }
      if (!email.includes("@")) {
        return {success: false, error: "Invalid email format"};
      }
      if (password.length < 6) {
        return {success: false, error: "Password must be at least 6 characters long"};
      }

      const userCredential = await createUserWithEmailAndPassword(this.service, email, password);
      
      if (userCredential.user) {
        this.analytics.setUserId(userCredential.user.uid);
        this.analytics.logEvent(event.SignUp);
        return {success: true, user: userCredential.user};
      }
      
      return {success: false, error: "Failed to create user"};
    } catch (error) {
      console.error("Sign up failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Sign up failed";
      this.analytics.logEvent(event.SignUp); // Log the attempt even if it failed
      return {success: false, error: errorMessage};
    }
  }


  /**
   * Signs in a user with the provided email and password.
   * @param email - The email of the user.
   * @param password - The password of the user.
   * @returns A promise that resolves with the user object when the sign-in is successful, or rejects with an error.
   */
  async signIn(
    email: string,
    password: string,
  ): Promise<{
    credentials: UserCredential | null;
    accessToken: string | null;
    error?: string;
  }> {
    try {
      // Input validation
      if (!email || !password) {
        throw new Error("Email and password are required");
      }
      if (!email.includes("@")) {
        throw new Error("Invalid email format");
      }


      const credentials = await signInWithEmailAndPassword(
        this.service,
        email,
        password,
      );
      
      if (credentials?.user) {
        try {
          // Get access token
          this.accessToken = await credentials.user.getIdToken();
          const refreshToken = credentials.user.refreshToken;

          useCookie("accessToken").value = this.accessToken;
          useCookie("refreshToken").value = refreshToken;


          // Set analytics user ID
          this.analytics.setUserId(credentials.user.uid);
          this.analytics.logEvent(event.SignIn);
        } catch (tokenError) {
          console.error("Token setup failed:", tokenError);
          return {credentials: null, accessToken: null, error: "Token setup failed"};
        }
      }
      
      return {credentials, accessToken: this.accessToken};
    } catch (error) {
      console.error("Sign in failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Sign in failed";
      return {credentials: null, accessToken: null, error: errorMessage};
    }
  }

  /**
   * Signs out the currently authenticated user.
   * @returns A promise that resolves when the sign-out is successful, or rejects with an error.
   */
  async signOut(): Promise<{success: boolean; error?: string}> {
    try {

      // Clear stored tokens and user data
      this.accessToken = null;
      this.firebaseUser = null;
      this.loggedIn = false;
      this.user = null;

      // Clear cookies
      useCookie("accessToken").value = null;
      useCookie("refreshToken").value = null;

      // Sign out from Firebase
      await this.service.signOut();
      
      this.analytics.logEvent(event.SignOut);
      return {success: true};
    } catch (error) {
      console.error("Sign out failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Sign out failed";
      return {success: false, error: errorMessage};
    }
  }

  /**
   * Verifies the password reset code sent to the user's email address.
   * @param code - The password reset code.
   * @returns A promise that resolves with the email when verification is successful, or rejects with an error.
   */
  async verifyPasswordResetCode(code: string): Promise<{success: boolean; email?: string; error?: string}> {
    try {
      // Input validation
      if (!code || code.trim().length === 0) {
        return {success: false, error: "Password reset code is required"};
      }

      const email = await verifyPasswordResetCode(this.service, code);
      this.analytics.logEvent(event.PasswordResetVerified);
      return {success: true, email};
    } catch (error) {
      console.error("Password reset code verification failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Password reset code verification failed";
      this.analytics.logEvent(event.PasswordResetVerified); // Log the attempt even if it failed
      return {success: false, error: errorMessage};
    }
  }

  /**
   * Confirms the password reset using the code sent to the user's email address.
   * @param code - The password reset code.
   * @param password - The new password.
   * @returns A promise that resolves when the password reset is confirmed successfully, or rejects with an error.
   */
  async confirmPasswordReset(code: string, password: string): Promise<{success: boolean; error?: string}> {
    try {
      // Input validation
      if (!code || code.trim().length === 0) {
        return {success: false, error: "Password reset code is required"};
      }
      if (!password) {
        return {success: false, error: "New password is required"};
      }
      if (password.length < 6) {
        return {success: false, error: "Password must be at least 6 characters long"};
      }

      await confirmPasswordReset(this.service, code, password);
      this.analytics.logEvent(event.PasswordResetConfirmed);
      return {success: true};
    } catch (error) {
      console.error("Password reset confirmation failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Password reset confirmation failed";
      this.analytics.logEvent(event.PasswordResetConfirmed); // Log the attempt even if it failed
      return {success: false, error: errorMessage};
    }
  }

  // /**
  //  * Sends a password reset email to the provided email address.
  //  * @param email - The email address of the user.
  //  * @returns A promise that resolves when the password reset email is sent successfully, or rejects with an error.
  //  */
  // async sendPasswordResetLink(email: string): Promise<void> {
  //   await User.SendPasswordResetLink(email).finally(() => {
  //     this.analytics.logEvent(event.PasswordResetRequested);
  //   });
  // }

  /**
   * Retrieves the currently authenticated user.
   * @returns A promise that resolves with the user object, or rejects with an error.
   */
  async getUser(): Promise<unknown> {
    return this.service.currentUser;
  }

  /**
   * Retrieves the user profile for the currently authenticated user.
   * @returns A promise that resolves when the user profile is retrieved successfully, or rejects with an error.
   */
  async getUserProfile(): Promise<{success: boolean; error?: string}> {
    try {
      if (!this.firebaseUser?.uid) {
        return {success: false, error: "No authenticated user found"};
      }

      this.user = new User();
      const result = await this.user.get(this.firebaseUser.uid);
      
      if (!result.success) {
        return {success: false, error: result.error};
      }
      
      return {success: true};
    } catch (error) {
      console.error("Failed to get user profile:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to get user profile";
      return {success: false, error: errorMessage};
    }
  }

  /**
   * Refresh the access token for the currently authenticated user.
   * @returns A promise that resolves when the access token is refreshed successfully, or rejects with an error.
   */
  async refreshToken(): Promise<{success: boolean; error?: string}> {
    try {
      if (!this.firebaseUser) {
        return {success: false, error: "No authenticated user found"};
      }

      const accessToken = await this.firebaseUser.getIdToken(true);
      if (accessToken) {
        this.accessToken = accessToken;
        useCookie("accessToken").value = this.accessToken;
        return {success: true};
      }

      return {success: false, error: "Failed to get access token"};
    } catch (error) {
      console.error("Token refresh failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Token refresh failed";
      return {success: false, error: errorMessage};
    }
  }


  /**
   * Waits for the authentication status to change and invokes the appropriate callback.
   *
   * @param successCallback - The callback function to be called when the authentication status changes to successful.
   * @param errorCallback - The callback function to be called when the authentication status changes to an error.
   * @returns A promise that resolves when the authentication status changes.
   */
  async waitForStatus(
    successCallback: (auth: Authentication) => void,
    errorCallback: (auth: Authentication, error: AxiosError) => void,
  ): Promise<void> {
    try {
      onAuthStateChanged(this.service, async (firebaseUser) => {
        try {
          this.firebaseUser = firebaseUser;
          this.loggedIn = firebaseUser !== null;
          
          if (this.loggedIn && firebaseUser) {

            // Get access token properly
            try {
              this.accessToken = await firebaseUser.getIdToken();
            } catch (tokenError) {
              console.error("Failed to get access token:", tokenError);
              errorCallback(this, tokenError as AxiosError);
              return;
            }


            // Set user ID for analytics
            if (firebaseUser.uid) {
              this.analytics.setUserId(firebaseUser.uid);
            }

            // Retrieve profile
            const profileResult = await this.getUserProfile();
            if (profileResult.success) {
              successCallback(this);
            } else {
              console.error("Failed to get user profile:", profileResult.error);
              const error = new Error(profileResult.error || "Failed to get user profile");
              errorCallback(this, error as AxiosError);
            }
          } else {
            // User is signed out
            successCallback(this);
          }
        } catch (error) {
          console.error("Auth state change error:", error);
          errorCallback(this, error as AxiosError);
        }
      });
    } catch (error) {
      console.error("Failed to set up auth state listener:", error);
      errorCallback(this, error as AxiosError);
    }
  }
}

export default function useAuth(
  app: FirebaseApp,
  analytics: Analytics,
): Authentication {
  return Authentication.getInstance(app, analytics);
}
